import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import MessagingModal from "./MessagingModal";
import { isPatientRole } from "../../config/roles";
import { loadCurrentStaffUser } from "../../utils/currentStaffUser";
import { onOpenMessages, onCloseMessages, emitMessagesClosed } from "../../utils/messagingBus";
import { startMessagingNotifications } from "../../utils/messagingNotifications";

// App-wide home of the Messages modal, mounted once in App.js on every route.
// The Navbar renders nothing on "/" and on /clinic/… chart routes, so it
// can't host the modal that a note's task link ("Open in Tasks") opens there.
// Opened with openMessages() from utils/messagingBus.
export default function MessagingHost() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [openRequest, setOpenRequest] = useState(null);

  useEffect(() => {
    if (location.pathname === "/403") return undefined;
    let cancelled = false;
    loadCurrentStaffUser()
      .then((u) => { if (!cancelled) setUser(u); })
      // A failed lookup (a network blip) says nothing about who's signed in:
      // keep the current user rather than tearing the modal down.
      .catch((err) => console.warn("Messaging: couldn't load the signed-in user", err));
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Another account signed in, or this one signed out: nothing from the last
  // session stays open or pending. Reset while rendering, so the next account
  // never gets even one render of the modal open on the last one's request.
  // (The first load, no one → a user, keeps a request made while it loaded.)
  const userId = user?.id ?? null;
  const [seenUserId, setSeenUserId] = useState(null);
  if (userId !== seenUserId) {
    setSeenUserId(userId);
    if (seenUserId !== null) {
      setIsOpen(false);
      setOpenRequest(null);
    }
  }

  useEffect(
    () =>
      onOpenMessages((detail) => {
        setOpenRequest({ tab: detail.tab || "chat", focus: detail.focus || null, nonce: `${Date.now()}-${Math.random()}` });
        setIsOpen(true);
      }),
    [],
  );

  // The tab's notifications feed and unread counts (utils/messagingNotifications.js), for a
  // signed-in staff account only and never on /403. Another account, or signing out, stops the
  // last one's (its counts, socket and notifications) before anything of the next one's starts.
  const notifyUserId =
    user && !isPatientRole(user.role) && location.pathname !== "/403" ? user.id : null;
  useEffect(() => (notifyUserId ? startMessagingNotifications(notifyUserId) : undefined), [notifyUserId]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    emitMessagesClosed();
  }, []);
  // closeMessages() from anywhere, e.g. a paperwork task's "Open in chart".
  useEffect(() => onCloseMessages(handleClose), [handleClose]);

  if (!user || isPatientRole(user.role)) return null;
  // Keyed by user: the next account starts with a fresh modal, never the last
  // one's selected conversation or loaded messages.
  return (
    <MessagingModal
      key={user.id}
      isOpen={isOpen}
      onClose={handleClose}
      currentUser={user}
      openRequest={openRequest}
    />
  );
}
